import { User } from "Models";
import { useAllUsers } from "modules/users/userHooks";
import { useMemo, useCallback, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Form } from "react-bootstrap";
import DataTable, { TableColumn } from "react-data-table-component";
import { useHistory, useLocation } from "react-router";
import { faFilter } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import UserManagementModal from "./UserManagementModal";

const DEFAULT_ROWS_PER_PAGE = 10;

const UsersTable: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [users] = useAllUsers();
  
  // Derive state from URL - URL is the source of truth
  const urlParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const roleFilterParam = params.get("roleFilter") || "";
    return {
      search: params.get("search") || "",
      roleFilter: roleFilterParam ? roleFilterParam.split(",").filter(Boolean) : [],
      page: parseInt(params.get("page") || "1", 10),
      rowsPerPage: parseInt(params.get("rowsPerPage") || String(DEFAULT_ROWS_PER_PAGE), 10)
    };
  }, [location.search]);

  const filterText = urlParams.search;
  const roleFilter = urlParams.roleFilter;
  const currentPage = urlParams.page;
  const rowsPerPage = urlParams.rowsPerPage;
  
  // State for dropdown visibility
  const [showRoleFilter, setShowRoleFilter] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Update dropdown position when button position changes
  useEffect(() => {
    if (showRoleFilter && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
  }, [showRoleFilter]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current?.contains(event.target as Node) ||
        dropdownRef.current?.contains(event.target as Node)
      ) {
        return;
      }
      setShowRoleFilter(false);
    };

    if (showRoleFilter) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showRoleFilter]);

  // Get all unique roles from users for the filter dropdown
  const uniqueRoles = useMemo(() => {
    const roleSet = new Set<string>();
    users?.forEach((user) => {
      user.roles?.forEach((role) => {
        if (role.description) {
          roleSet.add(role.description);
        }
      });
    });
    return Array.from(roleSet).sort();
  }, [users]);

  // Update URL when search changes
  const handleSearchChange = (value: string) => {
    const params = new URLSearchParams(location.search);
    if (value) {
      params.set("search", value);
    } else {
      params.delete("search");
    }
    // Reset to page 1 when searching
    params.delete("page");
    const newUrl = params.toString() 
      ? `${location.pathname}?${params.toString()}`
      : location.pathname;
    history.replace(newUrl);
  };

  // Toggle role in filter (multi-select)
  const handleRoleToggle = useCallback((roleDescription: string) => {
    const params = new URLSearchParams(location.search);
    const currentFilters = [...roleFilter];
    const index = currentFilters.indexOf(roleDescription);
    
    if (index > -1) {
      // Remove role from filter
      currentFilters.splice(index, 1);
    } else {
      // Add role to filter
      currentFilters.push(roleDescription);
    }
    
    if (currentFilters.length > 0) {
      params.set("roleFilter", currentFilters.join(","));
    } else {
      params.delete("roleFilter");
    }
    
    // Reset to page 1 when filtering
    params.delete("page");
    const newUrl = params.toString() 
      ? `${location.pathname}?${params.toString()}`
      : location.pathname;
    history.replace(newUrl);
  }, [location.pathname, location.search, history, roleFilter]);

  // Select all roles (including "No Roles")
  const handleSelectAll = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const allOptions = [...uniqueRoles, "__NO_ROLES__"];
    params.set("roleFilter", allOptions.join(","));
    params.delete("page");
    const newUrl = params.toString() 
      ? `${location.pathname}?${params.toString()}`
      : location.pathname;
    history.replace(newUrl);
  }, [location.pathname, location.search, history, uniqueRoles]);

  // Clear all role filters
  const handleClearAll = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete("roleFilter");
    params.delete("page");
    const newUrl = params.toString() 
      ? `${location.pathname}?${params.toString()}`
      : location.pathname;
    history.replace(newUrl);
  }, [location.pathname, location.search, history]);

  // Update URL when page changes
  const handlePageChange = useCallback((page: number) => {
    const params = new URLSearchParams(location.search);
    if (page > 1) {
      params.set("page", page.toString());
    } else {
      params.delete("page");
    }
    const newUrl = params.toString() 
      ? `${location.pathname}?${params.toString()}`
      : location.pathname;
    history.replace(newUrl);
  }, [location.pathname, location.search, history]);

  // Update URL when rows per page changes
  const handleRowsPerPageChange = useCallback((newRowsPerPage: number, page: number) => {
    const params = new URLSearchParams(location.search);
    if (newRowsPerPage !== DEFAULT_ROWS_PER_PAGE) {
      params.set("rowsPerPage", newRowsPerPage.toString());
    } else {
      params.delete("rowsPerPage");
    }
    // Update page if needed
    if (page > 1) {
      params.set("page", page.toString());
    } else {
      params.delete("page");
    }
    const newUrl = params.toString() 
      ? `${location.pathname}?${params.toString()}`
      : location.pathname;
    history.replace(newUrl);
  }, [location.pathname, location.search, history]);

  // Filter users by displayName, email, and role
  const filteredUsers = useMemo(() => {
    let filtered = users;

    // Filter by search text (displayName or email)
    if (filterText) {
      const searchLower = filterText.toLowerCase();
      filtered = filtered?.filter((user) =>
        user.displayName?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower)
      );
    }

    // Filter by role (multi-select)
    if (roleFilter.length > 0) {
      const hasNoRolesFilter = roleFilter.includes("__NO_ROLES__");
      const roleDescriptions = roleFilter.filter((r) => r !== "__NO_ROLES__");
      
      filtered = filtered?.filter((user) => {
        const hasRoles = user.roles && user.roles.length > 0;
        const hasMatchingRole = roleDescriptions.length > 0 && 
          user.roles?.some((role) => roleDescriptions.includes(role.description));
        
        if (hasNoRolesFilter && roleDescriptions.length === 0) {
          // Only filtering by "No Roles"
          return !hasRoles;
        } else if (hasNoRolesFilter && roleDescriptions.length > 0) {
          // Filtering by "No Roles" OR specific roles
          return !hasRoles || hasMatchingRole;
        } else {
          // Only filtering by specific roles
          return hasMatchingRole;
        }
      });
    }

    return filtered;
  }, [users, filterText, roleFilter]);

  const columns: TableColumn<User>[] = useMemo(
    () => [
      { 
        selector: (row) => row.displayName, 
        name: "Name",
        sortable: true,
      },
      { 
        selector: (row) => row.email, 
        name: "Email",
        sortable: true,
      },
      {
        name: (
          <div 
            style={{ display: "flex", alignItems: "center", gap: "8px", position: "relative" }}
          >
            <span>Roles</span>
            <div style={{ position: "relative" }}>
              <button
                ref={buttonRef}
                type="button"
                style={{
                  padding: "0",
                  border: "none",
                  background: "none",
                  color: roleFilter.length > 0 ? "#0d6efd" : "#6c757d",
                  textDecoration: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRoleFilter(!showRoleFilter);
                }}
              >
                <FontAwesomeIcon icon={faFilter} />
              </button>
              {showRoleFilter &&
                createPortal(
                  <div
                    ref={dropdownRef}
                    style={{
                      position: "fixed",
                      top: `${dropdownPosition.top}px`,
                      left: `${dropdownPosition.left}px`,
                      backgroundColor: "white",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)",
                      zIndex: 9999,
                      minWidth: "220px",
                      maxWidth: "280px",
                      maxHeight: "400px",
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Header */}
                    <div
                      style={{
                        padding: "12px 16px",
                        borderBottom: "1px solid #e8e8e8",
                        backgroundColor: "#f8f9fa",
                        borderTopLeftRadius: "8px",
                        borderTopRightRadius: "8px",
                        fontWeight: 600,
                        fontSize: "13px",
                        color: "#495057",
                      }}
                    >
                      Filter by Role
                    </div>
                    
                    {/* Action buttons */}
                    <div style={{ padding: "8px", borderBottom: "1px solid #e8e8e8" }}>
                      <div
                        style={{
                          padding: "6px 12px",
                          cursor: "pointer",
                          borderRadius: "4px",
                          fontSize: "13px",
                          color: "#0d6efd",
                          fontWeight: 500,
                          transition: "background-color 0.15s ease",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectAll();
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#e7f1ff";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        Select All
                      </div>
                      <div
                        style={{
                          padding: "6px 12px",
                          cursor: "pointer",
                          borderRadius: "4px",
                          fontSize: "13px",
                          color: "#dc3545",
                          fontWeight: 500,
                          transition: "background-color 0.15s ease",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearAll();
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#fff5f5";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        Clear All
                      </div>
                    </div>
                    
                    {/* Role options */}
                    <div style={{ padding: "4px 0" }}>
                      {/* No Roles option */}
                      <div
                        style={{
                          padding: "8px 16px",
                          cursor: "pointer",
                          transition: "background-color 0.15s ease",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRoleToggle("__NO_ROLES__");
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f8f9fa";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        <Form.Check
                          type="checkbox"
                          checked={roleFilter.includes("__NO_ROLES__")}
                          onChange={() => handleRoleToggle("__NO_ROLES__")}
                          label={
                            <span style={{ fontSize: "14px", color: "#6c757d" }}>
                              No Roles
                            </span>
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      
                      {/* Divider if there are roles */}
                      {uniqueRoles.length > 0 && (
                        <div style={{ 
                          borderBottom: "1px solid #e8e8e8", 
                          margin: "4px 0" 
                        }} />
                      )}
                      
                      {/* Regular role options */}
                      {uniqueRoles.map((roleDesc) => (
                        <div
                          key={roleDesc}
                          style={{
                            padding: "8px 16px",
                            cursor: "pointer",
                            transition: "background-color 0.15s ease",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRoleToggle(roleDesc);
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#f8f9fa";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                          }}
                        >
                          <Form.Check
                            type="checkbox"
                            checked={roleFilter.includes(roleDesc)}
                            onChange={() => handleRoleToggle(roleDesc)}
                            label={
                              <span style={{ fontSize: "14px", color: "#212529" }}>
                                {roleDesc}
                              </span>
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      ))}
                    </div>
                  </div>,
                  document.body
                )}
            </div>
          </div>
        ),
        cell: (row) => (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {row.roles && row.roles.length > 0 ? (
              row.roles.map((role, index) => (
                <span
                  key={role.id || index}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "2px 10px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: 500,
                    backgroundColor: "#e5e7eb",
                    color: "#374151",
                    whiteSpace: "nowrap",
                  }}
                >
                  {role.description}
                </span>
              ))
            ) : (
              <span style={{ color: "#9ca3af", fontSize: "14px" }}>No roles</span>
            )}
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
      },
      {
        cell: (row) => <UserManagementModal user={row} />,
        ignoreRowClick: true,
        button: false,
        allowOverflow: true,
      },
    ],
    [handleRoleToggle, handleSelectAll, handleClearAll, roleFilter, uniqueRoles, showRoleFilter]
  );

  return (
    <div className='m-4'>
      <DataTable
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Users</span>
            <Form.Control
              type='text'
              placeholder='Search users...'
              value={filterText}
              onChange={(e) => handleSearchChange(e.target.value)}
              style={{ maxWidth: "300px" }}
            />
          </div>
        }
        columns={columns}
        data={filteredUsers}
        dense
        highlightOnHover
        pagination
        paginationDefaultPage={currentPage}
        paginationPerPage={rowsPerPage}
        paginationRowsPerPageOptions={[10, 25, 50, 100]}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handleRowsPerPageChange}
        responsive
      />
    </div>
  );
};

export default UsersTable;
